import { deletePolicyMenuGroupHeaderText } from './generateDeletePolicyList.locstring.json';
import loc from 'owa-localize';
import ElcFolderType from 'owa-service/lib/contract/ElcFolderType';
import RetentionPolicyTag from 'owa-service/lib/contract/RetentionPolicyTag';
import RetentionTagType from 'owa-service/lib/contract/RetentionTagType';
import {
    IContextualMenuItem,
    ContextualMenuItemType,
} from 'office-ui-fabric-react/lib/components/ContextualMenu';
import { DELETE_INHERIT_SUBMENU_ITEM_IDENTIFIER } from './retentionPolicyConstants';
import createInheritSubMenuItem from './createInheritSubMenuItem';
import createHierarchialSubMenuDeleteItem, {
    HierarchyRetentionPolicyTag,
} from './createHierarchialSubMenuDeleteItem';

import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';

const styles = require('./SubMenuItem.scss');

/**
 * Utility to generate the delete retention policy list
 * @param deleteRetentionPolicyTagList delete policy retention tag list which are to be shown
 * @param showTagSelection whether to show a selected sub menu item as checked
 * @param currentDeletePolicyTag currently selected delete tag
 * @param folderType
 * @param distinguishedFolderId distinguished folder id for the folder/item
 * @param onDeletePolicyClickCallback
 */
export default function generateDeletePolicyList(
    deleteRetentionPolicyTagList: RetentionPolicyTag[],
    showTagSelection: boolean,
    currentDeletePolicyTag: RetentionTagType,
    folderType: ElcFolderType,
    distinguishedFolderId: DistinguishedFolderIdName,
    onDeletePolicyClickCallback: (
        ev: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item: IContextualMenuItem
    ) => void
): IContextualMenuItem[] {
    const currentDeleteTagValue =
        currentDeletePolicyTag && currentDeletePolicyTag.IsExplicit
            ? currentDeletePolicyTag.Value
            : null;
    return [
        {
            key: 'deleteSection',
            className: styles.sectionHeader,
            itemType: ContextualMenuItemType.Section,
            sectionProps: {
                title: loc(deletePolicyMenuGroupHeaderText),
                items: getDeleteListItemsFromPolicyTags(
                    deleteRetentionPolicyTagList,
                    showTagSelection,
                    currentDeleteTagValue,
                    folderType,
                    distinguishedFolderId,
                    onDeletePolicyClickCallback
                ),
            },
        },
    ];
}

function getDeleteListItemsFromPolicyTags(
    retentionPolicyTagList: RetentionPolicyTag[],
    showTagSelection: boolean,
    currentDeleteTagValue: string,
    folderType: ElcFolderType,
    distinguishedFolderId: DistinguishedFolderIdName,
    onDeletePolicyClickCallback: (
        ev: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item: IContextualMenuItem
    ) => void
): IContextualMenuItem[] {
    let selectedPolicyFound = false;
    const isArchiveFolder = distinguishedFolderId === 'archive';
    // We only allow applying personal delete policy on personal folders. Handle the archive folder separately
    // as ElcFolderType does not provide support for that.
    const initialListItems =
        folderType != ElcFolderType.Personal || isArchiveFolder
            ? []
            : buildHierarchyPolicyTags(retentionPolicyTagList).map(
                  (hierarchyTag: HierarchyRetentionPolicyTag) => {
                      const rowInfo = createHierarchialSubMenuDeleteItem(
                          hierarchyTag,
                          showTagSelection,
                          currentDeleteTagValue,
                          onDeletePolicyClickCallback
                      );
                      if (rowInfo.policyFound) {
                          selectedPolicyFound = true;
                      }
                      return rowInfo.item;
                  }
              );

    return initialListItems.concat(
        createInheritSubMenuItem({
            retentionId: DELETE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
            retentionPolicyTags: retentionPolicyTagList,
            folderType: folderType,
            isArchive: false,
            isChecked: showTagSelection && !selectedPolicyFound,
            onClickCallback: onDeletePolicyClickCallback,
        })
    );
}

function buildHierarchyPolicyTags(
    retentionPolicyTagList: RetentionPolicyTag[]
): HierarchyRetentionPolicyTag[] {
    const hierarchyIndex = {};
    // RetentionId is the label Guid
    // Parent Label Identity is the parent retention id
    // Add root tags.
    const hierarchyPolicyTags = retentionPolicyTagList
        .filter(
            (tag: RetentionPolicyTag) =>
                tag.ParentLabelIdentity == null || tag.ParentLabelIdentity.length <= 0
        )
        .reduce((acc: HierarchyRetentionPolicyTag[], tag: RetentionPolicyTag) => {
            const hierarchyTag = { parentPolicyTag: tag, childrenPolicyTags: [] };
            acc.push(hierarchyTag);
            hierarchyIndex[tag.RetentionId] = hierarchyTag;
            return acc;
        }, []);

    // Add children tags
    retentionPolicyTagList
        .filter(
            (tag: RetentionPolicyTag) =>
                tag.ParentLabelIdentity != null &&
                tag.ParentLabelIdentity.length > 0 &&
                tag.ParentLabelIdentity in hierarchyIndex
        )
        .forEach((tag: RetentionPolicyTag) => {
            // Update the children policy tags of the hierarchy tag
            const hierarchyTag = hierarchyIndex[tag.ParentLabelIdentity];
            hierarchyTag.childrenPolicyTags.push(tag);
        });
    return hierarchyPolicyTags;
}
